/**
 * The calendar's date arithmetic and repeat presets, with no DOM and no network so it can be
 * tested under Node.
 *
 * Two kinds of time meet here and must never be confused:
 *  - A TIMED event is an instant (an ISO string from the API), shown in the browser's local zone.
 *  - An ALL-DAY event is a calendar DATE (`YYYY-MM-DD`), shown as that date whatever the zone.
 *    Its end is EXCLUSIVE in the API (an event on the 25th ends on the 26th); the form shows and
 *    takes the last day instead, and this module converts at the edge.
 *
 * "Local" throughout means the browser's zone, except for repeat presets, which are built from
 * London's clock (see londonParts): the server judges whether a rule fits its start on London's
 * clock, so the client must derive the weekday and day-of-month the same way or a late-evening
 * event abroad would be refused.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n) => String(n).padStart(2, '0');
const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/** Local `YYYY-MM-DD` for a Date. */
export function dateKey(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `YYYY-MM-DD` as local midnight. (`new Date('2026-12-25')` would be UTC midnight, the wrong day west of Greenwich.) */
export function parseDateOnly(value) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
}

export function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar-day arithmetic: 25 December + 1 is 26 December even across a clock change. */
export function addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Adds days to a `YYYY-MM-DD` string with no zone in play at all. */
export function addDaysToDateString(value, days) {
    const [y, m, d] = value.split('-').map(Number);
    const shifted = new Date(Date.UTC(y, m - 1, d + days));
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

export function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(date, months) {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/** Weeks start on Monday, as they do in the UK. */
function mondayOnOrBefore(date) {
    const daysSinceMonday = (date.getDay() + 6) % 7;
    return addDays(startOfDay(date), -daysSinceMonday);
}

/** Every day the month grid shows: whole weeks, Monday first, covering the month. */
export function monthGridDays(cursor) {
    const first = startOfMonth(cursor);
    const start = mondayOnOrBefore(first);
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const weeks = Math.ceil(((first.getDay() + 6) % 7 + daysInMonth) / 7);
    return Array.from({ length: weeks * 7 }, (_, i) => addDays(start, i));
}

/** The window to ask the API for: the grid's first local midnight to the one after its last day. */
export function gridWindow(cursor) {
    const days = monthGridDays(cursor);
    return { from: days[0], to: addDays(days[days.length - 1], 1) };
}

/**
 * The local days an occurrence touches, plus its exact times for a timed one.
 * A timed occurrence ending exactly at midnight does not touch the next day.
 */
export function occurrenceSpan(occurrence, allDay) {
    if (allDay) {
        const first = parseDateOnly(occurrence.starts_at);
        const last = addDays(parseDateOnly(occurrence.ends_at), -1);
        return { first, last: last < first ? first : last, start: null, end: null };
    }
    const start = new Date(occurrence.starts_at);
    const end = new Date(occurrence.ends_at);
    const lastMoment = new Date(Math.max(start.getTime(), end.getTime() - 1));
    return { first: startOfDay(start), last: startOfDay(lastMoment), start, end };
}

/**
 * Files every occurrence under each local day it touches, restricted to `days`.
 * Within a day, all-day events come first, then by start time, then by title.
 * @returns {Map<string, Array<{event: object, occurrence: object, allDay: boolean, start: Date|null, end: Date|null}>>}
 */
export function placeOccurrences(events, days) {
    const wanted = new Set(days.map(dateKey));
    const byDay = new Map(days.map((d) => [dateKey(d), []]));

    for (const event of events) {
        for (const occurrence of event.occurrences ?? []) {
            const span = occurrenceSpan(occurrence, event.all_day);
            for (let day = span.first; day <= span.last; day = addDays(day, 1)) {
                const key = dateKey(day);
                if (wanted.has(key)) byDay.get(key).push({ event, occurrence, allDay: event.all_day, start: span.start, end: span.end });
            }
        }
    }

    for (const entries of byDay.values()) {
        entries.sort((a, b) => {
            if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
            const byStart = (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0);
            return byStart || a.event.title.localeCompare(b.event.title);
        });
    }
    return byDay;
}

/**
 * The next few things happening from `from` over `days` days, soonest first, for a glance on the
 * home page. An event spanning several days appears once, on the first day of the window it is
 * seen on, and anything that has already finished today is not "coming up".
 *
 * The TypeScript twin of this lives in mobile/src/lib/calendar.ts; the two must agree.
 *
 * @returns {Array<{day: Date, entry: {event: object, occurrence: object, allDay: boolean, start: Date|null, end: Date|null}}>}
 */
export function upcomingEntries(events, from, days, limit) {
    const window = Array.from({ length: days }, (_, i) => addDays(startOfDay(from), i));
    const placed = placeOccurrences(events, window);
    const seen = new Set();
    const out = [];
    const now = from.getTime();

    for (const day of window) {
        for (const entry of placed.get(dateKey(day)) ?? []) {
            if (entry.end && entry.end.getTime() <= now) continue;
            const id = `${entry.event.id}:${entry.occurrence.starts_at}`;
            if (seen.has(id)) continue;
            seen.add(id);
            out.push({ day, entry });
            if (out.length >= limit) return out;
        }
    }
    return out;
}

/**
 * `datetime-local` takes `YYYY-MM-DDTHH:mm` in LOCAL time with no zone, so a Date has to be
 * converted rather than sliced from its ISO string, which is UTC.
 */
export function toLocalInputValue(date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** The form's start and end values -> what the API takes. An all-day form holds the LAST day; the API wants the day after. */
export function toApiTimes({ allDay, start, end }) {
    if (allDay) return { starts_at: start, ends_at: addDaysToDateString(end, 1) };
    return { starts_at: new Date(start).toISOString(), ends_at: new Date(end).toISOString() };
}

/** An API event -> the form's start and end values. The inverse of toApiTimes. */
export function toFormTimes(event) {
    if (event.all_day) return { start: event.starts_at, end: addDaysToDateString(event.ends_at, -1) };
    return { start: toLocalInputValue(new Date(event.starts_at)), end: toLocalInputValue(new Date(event.ends_at)) };
}

/** London's reading of an instant: weekday code, day of month, and `YYYY-MM-DD`. */
export function londonParts(date) {
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric',
        }).formatToParts(date).map((p) => [p.type, p.value]),
    );
    const code = { Sun: 'SU', Mon: 'MO', Tue: 'TU', Wed: 'WE', Thu: 'TH', Fri: 'FR', Sat: 'SA' }[parts.weekday];
    return { weekday: code, day: Number(parts.day), date: `${parts.year}-${pad(Number(parts.month))}-${pad(Number(parts.day))}` };
}

/** The weekday and day of month of an event's first day: London's clock for a timed one, the date itself for all-day. */
function startParts({ allDay, start }) {
    if (allDay) {
        const [y, m, d] = start.split('-').map(Number);
        return { weekday: WEEKDAY_CODES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()], day: d };
    }
    const { weekday, day } = londonParts(new Date(start));
    return { weekday, day };
}

export const REPEATS = ['none', 'daily', 'weekly', 'monthly', 'yearly'];

/**
 * A repeat choice -> the rule to send (or null). `start` is the form's start value: a date for
 * all-day, a `datetime-local` value otherwise. `until` is a `YYYY-MM-DD` last day, or ''.
 * The server re-validates and canonicalises whatever this sends; this only has to be close.
 */
export function rruleFromRepeat({ repeat, until, allDay, start }) {
    if (repeat === 'none' || !REPEATS.includes(repeat)) return null;
    const parts = [`FREQ=${repeat.toUpperCase()}`];
    const { weekday, day } = startParts({ allDay, start });
    if (repeat === 'weekly') parts.push(`BYDAY=${weekday}`);
    if (repeat === 'monthly') parts.push(`BYMONTHDAY=${day}`);
    if (until) parts.push(`UNTIL=${until.replaceAll('-', '')}`);
    return parts.join(';');
}

/**
 * A stored rule -> the form's repeat choice. Anything the presets could not have produced
 * (an interval, several weekdays, an ordinal) is `custom`, kept exactly as it is on save
 * rather than flattened into a preset that means something different.
 */
export function repeatFromRrule(rrule, { allDay }) {
    if (!rrule) return { repeat: 'none', until: '', custom: false };
    const parts = new Map(rrule.split(';').map((p) => p.split('=')));
    const freq = parts.get('FREQ');
    const repeat = freq?.toLowerCase();
    const allowed = new Set(['FREQ', 'UNTIL', repeat === 'weekly' ? 'BYDAY' : '', repeat === 'monthly' ? 'BYMONTHDAY' : '']);
    const simple = REPEATS.includes(repeat)
        && [...parts.keys()].every((key) => allowed.has(key))
        && (parts.get('BYDAY') ?? '').split(',').length <= 1
        && (parts.get('BYMONTHDAY') ?? '').split(',').length <= 1;
    if (!simple) return { repeat: 'none', until: '', custom: true };

    let until = '';
    const raw = parts.get('UNTIL');
    if (raw) {
        // A timed rule's UNTIL is UTC: show the London date it falls on, which is what was typed.
        until = allDay || raw.length === 8
            ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`
            : londonParts(new Date(`${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(9, 11)}:${raw.slice(11, 13)}:${raw.slice(13, 15)}Z`)).date;
    }
    return { repeat, until, custom: false };
}

/** A rule in words, for the detail view. */
export function describeRepeat(rrule) {
    if (!rrule) return '';
    const { repeat, until, custom } = repeatFromRrule(rrule, { allDay: rrule.includes('UNTIL=') && !rrule.includes('T') });
    if (custom) return 'Repeats on a custom schedule';
    const every = { daily: 'Every day', weekly: 'Every week', monthly: 'Every month', yearly: 'Every year' }[repeat];
    if (!until) return every;
    const label = parseDateOnly(until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${every}, until ${label}`;
}
